interface PendingTokenRequest {
  resolve: () => void;
  reject: (reason: unknown) => void;
  signal?: AbortSignal;
  abortHandler?: () => void;
}

/**
 * In-memory token bucket shared by all consumers in the current browser tab.
 */
export class TokenBucket {
  private tokens: number;
  private lastRefillAt: number;
  private readonly queue: PendingTokenRequest[] = [];
  private refillTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly capacity: number,
    private readonly refillRatePerSecond: number,
  ) {
    if (capacity <= 0 || refillRatePerSecond <= 0) {
      throw new Error("Token bucket capacity and refill rate must be positive");
    }

    this.tokens = capacity;
    this.lastRefillAt = Date.now();
  }

  acquire(signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return Promise.reject(abortError());

    this.refill();
    if (this.queue.length === 0 && this.tokens >= 1) {
      this.tokens -= 1;
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const request: PendingTokenRequest = { resolve, reject, signal };
      request.abortHandler = () => {
        const index = this.queue.indexOf(request);
        if (index === -1) return;
        this.queue.splice(index, 1);
        reject(abortError());
        this.scheduleRefill();
      };
      signal?.addEventListener("abort", request.abortHandler, { once: true });
      this.queue.push(request);
      this.scheduleRefill();
    });
  }

  private refill() {
    const now = Date.now();
    const elapsedMs = Math.max(0, now - this.lastRefillAt);
    this.tokens = Math.min(
      this.capacity,
      this.tokens + (elapsedMs * this.refillRatePerSecond) / 1_000,
    );
    this.lastRefillAt = now;
  }

  private drainQueue() {
    this.refill();

    while (this.tokens >= 1 && this.queue.length > 0) {
      const request = this.queue.shift();
      if (!request) break;
      request.signal?.removeEventListener("abort", request.abortHandler!);
      this.tokens -= 1;
      request.resolve();
    }

    this.scheduleRefill();
  }

  private scheduleRefill() {
    if (this.refillTimer) {
      clearTimeout(this.refillTimer);
      this.refillTimer = null;
    }
    if (this.queue.length === 0) return;

    this.refill();
    const delayMs = Math.max(
      1,
      Math.ceil(((1 - this.tokens) * 1_000) / this.refillRatePerSecond),
    );
    this.refillTimer = setTimeout(() => {
      this.refillTimer = null;
      this.drainQueue();
    }, delayMs);
  }
}

function abortError() {
  return new DOMException("The operation was aborted", "AbortError");
}
